/* ==========================================================================
   KITSTERS - MAGIC HOUR CART (kmc)
   --------------------------------------------------------------------------
   Drives snippets/kitsters-magic-cart.liquid - the localStorage cart that
   exists only on the two Magic Hour templates. See that file's header for
   the what and the why; this header is the how.

   THE CART IS NOT THE SHOPIFY CART. Lines live in localStorage
   (`kt_magic_cart_v1`), so while the shopper browses, nothing here touches
   /cart/*.js - a Magic Hour kit can never surface in the Kalles drawer, the
   header badge or /cart (owner: "magic hour product never go to our cart").
   The real cart is written exactly once, at checkout hand-off, and the first
   thing the hand-off does is /cart/clear.js - which is BOTH halves of the
   owner's rule at once: only magic kits reach checkout, and any retail items
   the shopper had are out of the order. Identical to the MH PDP's own
   express path (assets/kitsters-magic-hour-product.js §5), which has shipped
   and been owner-verified; the billing/prefill logic below is a deliberate
   copy of that file's - if a rule changes there (company split, phone
   normalisation, the deliver-here tick), change it here in the same PR.

   Exposes window.KTMagicCart = { add, open, count } for the PDP's
   "Add to Magic Cart" button. Everything else is internal.

   Every DOM query is scoped to [data-kt-magic-cart]: the PDP renders the
   SAME data-field names inside its own panel, and the two forms coexist on
   that page precisely because neither script queries document for them.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'kt_magic_cart_v1';

  function readStore() {
    try {
      var d = JSON.parse(window.localStorage.getItem(KEY));
      if (d && Array.isArray(d.lines)) return d;
    } catch (e) { /* corrupt or blocked storage -> empty cart */ }
    return { lines: [] };
  }
  function writeStore(store) {
    try { window.localStorage.setItem(KEY, JSON.stringify(store)); }
    catch (e) { /* private mode / quota: the in-memory cart still works */ }
  }

  function init() {
    var root = document.querySelector('[data-kt-magic-cart]');
    if (!root) return;

    /* ── PACKS, not kits (owner 2026-08-20) ────────────────────────────────
       A Magic Hour variant is now a PACK OF FIVE - repriced 589 -> 2945 in
       Admin the same day - so every quantity in this file, `line.qty` included,
       is a pack count handed to /cart/add.js unchanged. `PACK` is the ONE
       conversion: multiply by it wherever a figure has to be printed in kits.
       MIN / STEP / LINE_MAX / MAX_TOTAL are therefore all in PACKS. */
    var PACK = parseInt(root.getAttribute('data-pack'), 10) || 5;
    var MIN = parseInt(root.getAttribute('data-min'), 10) || 1;
    var STEP = parseInt(root.getAttribute('data-step'), 10) || 1;
    var LINE_MAX = parseInt(root.getAttribute('data-line-max'), 10) || 10;
    /* The ONLINE cap is for the ORDER, not the line: past it the offer is a
       quote conversation (volume pricing, multi-address dispatch), so checkout
       refuses above it rather than quietly taking an order the page copy says
       is a conversation. In packs here; the message it produces quotes kits,
       because that is the number the copy and the FAQ use. */
    var MAX_TOTAL = parseInt(root.getAttribute('data-max-total'), 10) || 10;
    var supportEmail = root.getAttribute('data-support-email') || '';
    var primaryHost = '';
    try { primaryHost = new URL(root.getAttribute('data-primary-origin')).host; }
    catch (e) { primaryHost = ''; }

    var pill = root.querySelector('[data-kmc-pill]');
    var pillCount = root.querySelector('[data-kmc-pill-count]');
    var pillKits = root.querySelector('[data-kmc-pill-kits]');
    var pairsWrap = root.querySelector('[data-kmc-pairs]');
    var pillTotal = root.querySelector('[data-kmc-pill-total]');
    var drawer = root.querySelector('[data-kmc-drawer]');
    var linesEl = root.querySelector('[data-kmc-lines]');
    var emptyEl = root.querySelector('[data-kmc-empty]');
    var summaryEl = root.querySelector('[data-kmc-summary]');
    var subLabel = root.querySelector('[data-kmc-sub-label]');
    var subEl = root.querySelector('[data-kmc-sub]');
    var grandEl = root.querySelector('[data-kmc-grand]');
    var lineTpl = root.querySelector('[data-kmc-line-template]');
    var orderOpenBtn = root.querySelector('[data-kmc-order-open]');
    var detailsForm = root.querySelector('[data-kmc-details]');
    var buyBtn = root.querySelector('[data-kmc-buy-now]');
    var buyLabel = root.querySelector('[data-kmc-buy-label]');
    var errorEl = root.querySelector('[data-kmc-error]');

    var store = readStore();

    /* Money formatting and the tax model are IDENTICAL to the MH PDP's - see
       that file's note. In short: a US store is tax-exclusive, so the stored
       price is the price, there is nothing to take back out, and no US sales tax is
       whatever checkout works out from the delivery address. */
    function money(cents) {
      return window.KitstersMoney.format(cents);
    }

    function snap(n, min, max) {
      if (!isFinite(n)) return min;
      if (n <= min) return min;
      var snapped = min + Math.round((n - min) / STEP) * STEP;
      return snapped > max ? max : snapped;
    }
    function lineMin(line) { return parseInt(line.min, 10) || MIN; }
    function lineMax(line) { return parseInt(line.max, 10) || LINE_MAX; }

    /* `qty` is packs, so these are two different numbers now and the names say
       which is which. Every printed "N kits" goes through totalKits(). */
    function totalPacks() {
      return store.lines.reduce(function (sum, l) { return sum + l.qty; }, 0);
    }
    function totalKits() { return totalPacks() * PACK; }
    function totalPaise() {
      return store.lines.reduce(function (sum, l) { return sum + l.qty * l.unit; }, 0);
    }

    /* ── Render ──────────────────────────────────────────────────────────
       The whole cart re-renders on every change - it is a handful of lines,
       and one honest rebuild beats incremental DOM surgery for something
       this small. Titles, figures and URLs are written with textContent /
       setAttribute, never innerHTML: a stored string can only ever be text. */
    function renderLine(line) {
      var li = lineTpl.content.firstElementChild.cloneNode(true);
      var img = li.querySelector('[data-line-img]');
      if (line.image) { img.setAttribute('src', line.image); img.setAttribute('alt', line.title); }
      else { img.closest('[data-line-link]').hidden = true; }
      li.querySelectorAll('[data-line-link]').forEach(function (a) {
        if (line.url) a.setAttribute('href', line.url);
      });
      li.querySelector('[data-line-title]').textContent = line.title;
      /* Per-KIT figure first, exactly as the PDP's buy card leads with it
         (owner: "first view, we only mention price/kit i.e 499"), then what a
         pack costs. `line.unit` is the PACK price, so the per-kit figure is a
         division - never a second stored number. */
      /* No tax wording here: the totals panel a few centimetres below says
         where tax is worked out, and repeating it wrapped this onto two lines in a
         440px drawer (measured). One figure, one home. */
      li.querySelector('[data-line-unit]').textContent =
        money(Math.round(line.unit / PACK)) + ' / kit \u00b7 ' +
        money(line.unit) + ' a pack of ' + PACK;
      li.querySelector('[data-line-total]').textContent = money(line.qty * line.unit);

      var qtyInput = li.querySelector('[data-line-qty]');
      var dec = li.querySelector('[data-line-dec]');
      var inc = li.querySelector('[data-line-inc]');
      qtyInput.value = line.qty;
      var lineKits = li.querySelector('[data-line-kits]');
      if (lineKits) lineKits.textContent = '= ' + (line.qty * PACK) + ' kits';
      dec.disabled = line.qty <= lineMin(line);
      inc.disabled = line.qty >= lineMax(line);

      dec.addEventListener('click', function () { setQty(line.id, line.qty - STEP); });
      inc.addEventListener('click', function () { setQty(line.id, line.qty + STEP); });
      ['change', 'blur'].forEach(function (evt) {
        qtyInput.addEventListener(evt, function () {
          setQty(line.id, parseInt(qtyInput.value, 10));
        });
      });
      li.querySelector('[data-line-remove]').addEventListener('click', function () {
        removeLine(line.id);
      });
      return li;
    }

    function render() {
      var packs = totalPacks();
      var kits = totalKits();
      var total = totalPaise();
      var hasLines = store.lines.length > 0;

      /* Pill: hidden empty, Zomato-style otherwise. */
      if (pill) {
        pill.hidden = !hasLines;
        if (pillCount) pillCount.textContent = String(store.lines.length);
        if (pillKits) pillKits.textContent = kits + (kits === 1 ? ' kit' : ' kits');
        if (pillTotal) pillTotal.textContent = money(total);
      }

      if (emptyEl) emptyEl.hidden = hasLines;
      if (summaryEl) summaryEl.hidden = !hasLines;
      if (linesEl) {
        linesEl.textContent = '';
        store.lines.forEach(function (line) { linesEl.appendChild(renderLine(line)); });
      }

      renderPairs();


      if (subLabel) {
        subLabel.textContent =
          packs + ' \u00d7 pack of ' + PACK + ' \u00b7 ' + kits + ' kits';
      }
      if (subEl) subEl.textContent = money(total);
      /* [data-kmc-gst] is static "Calculated at checkout" in the Liquid - there
         is no number to write into it. */
      if (grandEl) grandEl.textContent = money(total);
    }

    /* ── "Pairs well with" (owner 2026-08-20) ────────────────────────────
       The rail is server-rendered by snippets/kitsters-magic-cart.liquid from
       the owner's kit table; all this does is hide a card whose kit is already
       a line, and hide the rail entirely once nothing is left to offer. A
       cross-sell that recommends what the shopper has already chosen reads as
       broken, and the alternative - rebuilding the rail in JS - would put a
       second copy of the catalogue in a second language.

       Visibility is a CLASS on the item plus the `hidden` attribute, never an
       inline `display`: an inline style out-ranks every stylesheet rule and is
       invisible in the sheets you are staring at (§10.26). The CSS carries the
       matching `[hidden]` guard at its own specificity (§10.51). */
    function renderPairs() {
      if (!pairsWrap) return;
      var inCart = {};
      store.lines.forEach(function (l) { inCart[String(l.id)] = true; });

      var left = 0;
      pairsWrap.querySelectorAll('[data-kmc-pairs-item]').forEach(function (li) {
        var taken = !!inCart[li.getAttribute('data-id')];
        li.hidden = taken;
        if (!taken) left += 1;
      });
      pairsWrap.hidden = left === 0;
    }

    /* Delegated on the rail: one listener instead of eight, and it cannot miss
       a card a future change renders later. */
    if (pairsWrap) {
      pairsWrap.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-kmc-pairs-add]') : null;
        if (!btn || !pairsWrap.contains(btn)) return;
        e.preventDefault();

        var id = parseInt(btn.getAttribute('data-id'), 10) || 0;
        if (!id) return;

        window.KTMagicCart.add({
          id: id,
          qty: parseInt(btn.getAttribute('data-min'), 10) || MIN,
          unit: parseInt(btn.getAttribute('data-unit'), 10) || 0,
          title: btn.getAttribute('data-title') || 'Magic Hour kit',
          url: btn.getAttribute('data-url') || '',
          image: btn.getAttribute('data-image') || '',
          min: parseInt(btn.getAttribute('data-min'), 10) || MIN,
          max: parseInt(btn.getAttribute('data-max'), 10) || LINE_MAX
        });
      });
    }

    function persist() { writeStore(store); render(); }

    function setQty(id, qty) {
      var line = store.lines.find(function (l) { return l.id === id; });
      if (!line) return;
      line.qty = snap(qty, lineMin(line), lineMax(line));
      persist();
    }
    function removeLine(id) {
      store.lines = store.lines.filter(function (l) { return l.id !== id; });
      persist();
    }

    /* Another tab (LP in one, PDP in another) edits the same cart: re-read
       and re-render, or the two tabs show different carts until a reload. */
    window.addEventListener('storage', function (e) {
      if (e.key !== KEY) return;
      store = readStore();
      render();
    });

    /* ── Drawer - a PLAIN <dialog>, so the native API is correct (§10.70 is
       about Kalles components). Body scroll is parked behind our own class
       rather than Kalles' [scroll-lock] attribute - their cleanup gates on
       their own dialog stack, and borrowing their attribute would leave the
       page locked the first time their code reconciled it (§10.70b). The
       dialog's `close` event is the single cleanup point, so Esc, the X,
       and the backdrop all release the scroll the same way. */
    /* ── Open / close, with the exit animation (owner 2026-08-20) ─────────
       *"Cart closing animation should be left to right."* The panel now slides
       out to the right - the exact inverse of the entrance - instead of the
       dialog blinking away, which is what a native `close()` does on its own.

       THE ANIMATION IS THE REASON THIS IS MORE THAN TWO LINES. A <dialog> is
       removed from the top layer the instant `close()` returns, so the exit has
       to run BEFORE it, and everything that can go wrong while it runs has to
       be handled:

       · Idempotent entry. Kalles taught this the hard way (§10.70e): the drawer
         has two close buttons plus a backdrop click plus Esc, and a second
         close mid-animation would restart it - or, with a reversing transform,
         slide the panel back IN.
       · A watchdog. If `animationend` never fires - the element is display:none
         in some engine, the animation is overridden, the tab is backgrounded
         mid-flight - a modal <dialog> left open eats every click on the page
         and only a reload recovers it (§10.70b/d). The timeout is 3x the
         animation, and it calls the same `finish` the event does.
       · A re-open inside the exit window. `showModal()` on an already-open
         dialog throws, so the naive version swallows the click and the shopper
         taps twice (§10.70's second follow-on). Opening during a close
         finishes the close first, immediately.
       · prefers-reduced-motion closes outright. The CSS suppresses the
         keyframes there, so waiting for `animationend` would wait forever and
         only the watchdog would ever close it. */
    var kmcCloseTimer = null;

    function finishClose() {
      if (!drawer) return;
      if (kmcCloseTimer) { clearTimeout(kmcCloseTimer); kmcCloseTimer = null; }
      drawer.removeEventListener('animationend', onCloseAnimEnd);
      drawer.classList.remove('is-closing');
      if (drawer.open) drawer.close();
    }
    /* Named, so removeEventListener can actually remove it, and guarded on
       `e.target` because the panel's own children animate too (the gloss on
       the Order now button runs on a loop). */
    function onCloseAnimEnd(e) {
      if (e.target === drawer) finishClose();
    }

    function openDrawer() {
      if (!drawer) return;
      if (drawer.classList.contains('is-closing')) finishClose();
      if (drawer.open) return;
      try { drawer.showModal(); } catch (e) { return; }
      document.documentElement.classList.add('kmc-scroll-lock');
    }

    function closeDrawer() {
      if (!drawer || !drawer.open) return;
      if (drawer.classList.contains('is-closing')) return;

      var reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { drawer.close(); return; }

      drawer.classList.add('is-closing');
      drawer.addEventListener('animationend', onCloseAnimEnd);
      kmcCloseTimer = setTimeout(finishClose, 750);
    }
    if (drawer) {
      drawer.addEventListener('close', function () {
        document.documentElement.classList.remove('kmc-scroll-lock');
      });
      /* Backdrop click: a click whose target is the <dialog> itself landed on
         the backdrop/padding, never on the panel. */
      drawer.addEventListener('click', function (e) {
        if (e.target === drawer) closeDrawer();
      });
    }
    if (pill) pill.addEventListener('click', openDrawer);
    root.querySelectorAll('[data-kmc-close]').forEach(function (btn) {
      btn.addEventListener('click', closeDrawer);
    });

    /* "Order now" reveals the billing form, exactly like the PDP's panel; a
       second press submits rather than collapsing, so the button never feels
       like it undid the last press. */
    if (orderOpenBtn && detailsForm) {
      orderOpenBtn.addEventListener('click', function () {
        if (!detailsForm.hidden) {
          if (buyBtn) buyBtn.click();
          return;
        }
        detailsForm.hidden = false;
        orderOpenBtn.setAttribute('aria-expanded', 'true');
        var first = detailsForm.querySelector('input, select');
        detailsForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if (first) {
          setTimeout(function () {
            try { first.focus({ preventScroll: true }); } catch (e) { /* old Safari */ }
          }, 400);
        }
      });
    }

    /* ── Billing fields (scoped to the DRAWER's form, never document - the
       PDP's panel renders the same data-field names). Logic below mirrors
       kitsters-magic-hour-product.js §5 value-for-value; see this file's
       header for the keep-in-step rule. */
    function fieldValue(name) {
      var el = detailsForm ? detailsForm.querySelector('[data-field="' + name + '"]') : null;
      return el ? el.value.trim() : '';
    }
    function deliverHere() {
      var el = detailsForm ? detailsForm.querySelector('[data-field-deliver-here]') : null;
      return el ? !!el.checked : true;
    }
    function provinceCode() { return fieldValue('province'); }
    function provinceName() {
      var sel = detailsForm ? detailsForm.querySelector('[data-field="province"]') : null;
      if (!sel || sel.selectedIndex < 0) return '';
      var opt = sel.options[sel.selectedIndex];
      return opt && opt.value ? opt.textContent.trim() : '';
    }
    /* Normalise to E.164 for the value that goes ON THE ORDER, where nothing
       normalises it for us. India's version prepended +91 to any bare 10-digit
       number; on a US store that would stamp an Indian country code onto every
       American phone number an ops person later tries to call. */
    function normalisedPhone() {
      var raw = fieldValue('phone').replace(/[^\d+]/g, '');
      if (/^[2-9]\d{9}$/.test(raw)) return '+1' + raw;          // bare US 10-digit
      if (/^1[2-9]\d{9}$/.test(raw)) return '+' + raw;          // 1 + 10-digit
      return raw;                                               // already +CC, or something we should not guess at
    }
    function addressLine() {
      return [
        fieldValue('address1'),
        fieldValue('address2'),
        fieldValue('city'),
        provinceName(),
        fieldValue('zip'),
        fieldValue('country') || 'United States'
      ].filter(Boolean).join(', ');
    }
    function hoursSummary() {
      return store.lines.map(function (l) { return l.title + ' × ' + l.qty; }).join('; ');
    }
    function orderAttributes() {
      return {
        'Company name': fieldValue('company'),
        'Ordered by': fieldValue('contact_name'),
        'Designation': fieldValue('designation'),
        'Contact email': fieldValue('email'),
        'Contact phone': normalisedPhone(),
        'Registered address': addressLine(),
        'Deliver to registered address': deliverHere() ? 'Yes' : 'No',
        'Order type': 'Magic Hour cart - ' + store.lines.length +
          (store.lines.length === 1 ? ' hour' : ' hours'),
        'Hours': hoursSummary(),
        'Kits': String(totalKits()),
        'Packs': String(totalPacks()) + ' \u00d7 pack of ' + PACK
      };
    }
    function checkoutUrl() {
      var parts = [];
      function add(key, val) {
        if (!val) return;
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(val));
      }
      /* Company -> the checkout's NAME field, split at the last space:
         Shopify marks first AND last name required, and padding lastName
         with "." prints a stray dot on the GST invoice. */
      var company = fieldValue('company');
      var firstName = company;
      var lastName = '';
      var cut = company.lastIndexOf(' ');
      if (cut > 0) {
        firstName = company.slice(0, cut).trim();
        lastName = company.slice(cut + 1).trim();
      }
      add('checkout[email]', fieldValue('email'));
      add('checkout[shipping_address][first_name]', firstName);
      add('checkout[shipping_address][last_name]', lastName);
      add('checkout[shipping_address][company]', company);
      add('checkout[shipping_address][phone]', normalisedPhone());
      if (deliverHere()) {
        add('checkout[shipping_address][address1]', fieldValue('address1'));
        add('checkout[shipping_address][address2]', fieldValue('address2'));
        add('checkout[shipping_address][city]', fieldValue('city'));
        add('checkout[shipping_address][province]', provinceCode());
        add('checkout[shipping_address][zip]', fieldValue('zip'));
        add('checkout[shipping_address][country]', fieldValue('country') || 'United States');
      }
      return '/checkout' + (parts.length ? '?' + parts.join('&') : '');
    }
    function lineProperties(attrs, line) {
      var out = {};
      Object.keys(attrs).forEach(function (k) {
        if (k === 'Order type' || k === 'Hours' || k === 'Kits' || k === 'Packs') return;
        if (attrs[k]) out[k] = attrs[k];
      });
      out['Order type'] = 'Magic Hour - ' + line.title;
      out['Kits'] = String(line.qty * PACK);
      out['Packs'] = String(line.qty) + ' \u00d7 pack of ' + PACK;
      return out;
    }

    function showError(html) {
      if (!errorEl) return;
      errorEl.innerHTML = html;
      errorEl.hidden = false;
    }

    /* ── Pending state + every way back (§10.71): the hand-off navigates to
       Shopify checkout, and the shopper can come back via bfcache, a tab
       switch, or plain interaction. Same shape as the PDP's. */
    var pending = false;
    var pendingStarted = 0;
    var pendingTimer = null;
    var restoreArmed = false;
    var GRACE_MS = 1500;
    var restLabel = buyLabel ? buyLabel.textContent : 'Continue to payment';

    function backOnPage() { return Date.now() - pendingStarted > GRACE_MS; }
    function restoreBuy() {
      if (!pending) return;
      pending = false;
      if (buyBtn) buyBtn.disabled = false;
      if (buyLabel) buyLabel.textContent = restLabel;
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    function armRestore() {
      if (restoreArmed) return;
      restoreArmed = true;
      window.addEventListener('pageshow', function (e) { if (e.persisted) restoreBuy(); });
      document.addEventListener('visibilitychange', function () { if (!document.hidden) restoreBuy(); });
      window.addEventListener('focus', function () { if (backOnPage()) restoreBuy(); });
      document.addEventListener('pointerdown', function (e) { if (e.isTrusted && backOnPage()) restoreBuy(); }, true);
      document.addEventListener('keydown', function (e) { if (e.isTrusted && backOnPage()) restoreBuy(); }, true);
    }

    /* ── Checkout hand-off ────────────────────────────────────────────────
       clear -> attributes + note -> add EVERY magic line -> navigate. The
       clear is the "no normal product" guarantee: whatever the real cart
       held is gone before the magic lines land, so Shopify checkout can only
       ever see this order. The magic cart itself is deliberately NOT cleared
       here - the shopper may abandon checkout and come back, and an emptied
       cart on return reads as data loss. Shopify empties the real cart on a
       completed order; the magic copy is one Remove per line if they want it
       gone. */
    function startCheckout() {
      if (pending) return;
      var kits = totalKits();
      if (kits === 0) {
        showError('Your Magic Hour cart is empty - add an hour first.');
        return;
      }
      if (totalPacks() > MAX_TOTAL) {
        /* supportEmail comes from a theme setting via the snippet, never from
           shopper input, so building this anchor is safe. */
        var msg = 'I want to order ' + kits +
          ' Magic Hour kits for my team. Can we talk volume pricing?';
        showError(
          'Online orders stop at ' + (MAX_TOTAL * PACK) + ' kits - past that you get volume pricing and a curator. ' +
          '<a href="mailto:' + supportEmail + '?subject=' + encodeURIComponent(msg) +
          '">Get a quote by email</a> ' +
          'or trim the cart to ' + (MAX_TOTAL * PACK) + '.'
        );
        return;
      }

      pending = true;
      pendingStarted = Date.now();
      if (buyBtn) buyBtn.disabled = true;
      if (buyLabel) buyLabel.textContent = 'Processing…';
      if (errorEl) errorEl.hidden = true;
      armRestore();
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(restoreBuy, 30000);

      var attrs = orderAttributes();
      var noteLines = [
        'Magic Hour order - ' + store.lines.length +
          (store.lines.length === 1 ? ' hour' : ' hours') + ' from the Magic Hour cart',
        'Hours: ' + hoursSummary(),
        kits + ' kits to ONE delivery address',
        'Company: ' + (attrs['Company name'] || '-'),
        'Ordered by: ' + (attrs['Ordered by'] || '-') +
          (attrs['Designation'] ? ' (' + attrs['Designation'] + ')' : ''),
        'Contact: ' + [attrs['Contact email'], attrs['Contact phone']].filter(Boolean).join(' / '),
        'Registered address: ' + (attrs['Registered address'] || '-'),
        deliverHere()
          ? 'Deliver to the registered address above.'
          : 'DIFFERENT DELIVERY ADDRESS - see the shipping address on this order. The address above is the invoice address.'
      ].join('\n');

      /* Built BEFORE the network calls, so a malformed field fails here
         rather than after the cart has already been mutated. */
      var target = checkoutUrl();
      var items = store.lines.map(function (line) {
        return { id: line.id, quantity: line.qty, properties: lineProperties(attrs, line) };
      });

      fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('clear-failed'); })
        .then(function () {
          /* Attributes AFTER the clear - /cart/clear.js resets attributes and
             note along with the line items. */
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attrs, note: noteLines })
          });
        })
        .then(function (r) { if (!r.ok) throw new Error('attrs-failed'); })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (r) { if (!r.ok) throw new Error('add-failed'); return r.json(); })
        .then(function () {
          /* The page keeps rendering if checkout opens in an overlay or the
             shopper comes straight back - tell the real cart's UI what the
             cart now holds (§10.71). */
          if (window.KitstersCart) {
            window.KitstersCart.reload();
          }
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:count:update', {
                detail: { item_count: cart.item_count, total_price: cart.total_price }
              }));
            })
            .catch(function () {});

          /* Preview/dev hosts silently drop the ?checkout[…] prefill (the
             hop to checkout crosses a redirect that strips the query string)
             - warn rather than letting a preview look broken. Measured on
             the MH PDP 2026-08-19; same hand-off, same behaviour. */
          if (primaryHost && window.location.host !== primaryHost) {
            /* eslint-disable-next-line no-console */
            console.warn(
              '[kitsters] Checkout prefill will be DROPPED here. This page is on "' +
              window.location.host + '", not the storefront domain "' + primaryHost +
              '". Test on https://' + primaryHost + ' (a ?preview_theme_id= preview there is fine).'
            );
          }

          /* Stamp the hand-off for kitsters-magic-cart-guard.js: if the
             shopper abandons checkout, the kits are still in the REAL cart,
             and the stamp is what tells the sitewide guard to remove them on
             the next page view (owner: "magic hour product never go to our
             cart"). Cleared by the guard once the cart is clean. */
          try { window.localStorage.setItem('kt_magic_handoff', String(Date.now())); } catch (e) { /* no-op */ }

          /* A scripted navigation, never an <a href>: magic-shopify.js
             sweeps for a[href*="/checkout"] and would hijack a link into
             Magic Checkout, which drops every parameter (§10.74a). */
          window.location.assign(target);
        })
        .catch(function () {
          restoreBuy();
          showError('We could not start checkout. Check your connection and try again, or email us.');
        });
    }

    if (detailsForm) {
      /* The submit event, not a click handler: the browser runs the form's
         own required/pattern validation first. */
      detailsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        startCheckout();
      });
    }

    /* ── Public API (the PDP's Add to Magic Cart button) ─────────────────
       add() SETS the line to the quantity handed over rather than piling on
       top of an existing line: the PDP stepper reads as "I want N kits of
       this hour", and add-then-adjust-then-add-again doubling up is the
       surprise nobody wants. Adjusting further is what the drawer's own
       stepper is for. */
    window.KTMagicCart = {
      add: function (item) {
        if (!item || !item.id) return;
        var qty = snap(parseInt(item.qty, 10) || MIN,
          parseInt(item.min, 10) || MIN,
          parseInt(item.max, 10) || LINE_MAX);
        var line = store.lines.find(function (l) { return l.id === item.id; });
        if (line) {
          line.qty = qty;
          line.unit = parseInt(item.unit, 10) || line.unit;
          line.title = item.title || line.title;
          line.url = item.url || line.url;
          line.image = item.image || line.image;
        } else {
          store.lines.push({
            id: item.id,
            qty: qty,
            unit: parseInt(item.unit, 10) || 0,
            title: String(item.title || 'Magic Hour kit'),
            url: String(item.url || ''),
            image: String(item.image || ''),
            min: parseInt(item.min, 10) || MIN,
            max: parseInt(item.max, 10) || LINE_MAX
          });
        }
        persist();
      },
      open: openDrawer,
      count: totalKits
    };

    /* ── Revalidate stored lines against live product data ────────────────
       WHY THIS EXISTS. This cart is localStorage, and each line stores the PACK
       PRICE AT ADD TIME. Nothing re-checked it, so a line survived a reprice or
       an unpublish and kept rendering a figure the store would not honour.

       Measured 2026-08-30 on a real cart: a "$2,945.00 / $589.00 per kit" line
       for a product that is not in the catalogue at all, sitting directly above
       a "Pairs well with" rail reading $30 / kit — because that rail renders
       live Liquid prices while the line above it renders storage. 589 is the
       India rupee figure; the US price is 30. Nothing in the page was wrong.
       The storage was old.

       This is a money surface: checkout charges the REAL price, so a stale line
       is a number we show and do not charge. Correcting it is not cosmetic.

       Failure posture is deliberate. A 404 means the product is gone from this
       storefront, so the line cannot be bought and is dropped. Any OTHER failure
       — offline, 5xx, CORS — leaves the line exactly as it was: a network blip
       must never empty somebody's cart. And `fetch` is feature-detected, so a
       browser without it degrades to today's behaviour rather than throwing. */
    function revalidate() {
      if (!store.lines.length || typeof window.fetch !== 'function') return;
      var repriced = 0, dropped = 0;

      Promise.all(store.lines.map(function (line) {
        var url = String(line.url || '').split('?')[0].split('#')[0];
        if (!url) return null;
        return window.fetch(url + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function (p) {
            var v = (p.variants || []).filter(function (x) { return x.id === line.id; })[0];
            if (!v) { line._drop = true; dropped++; return; }
            /* price is in cents from /products/<handle>.js, the same unit as
               line.unit — verified against the live store before relying on it. */
            if (typeof v.price === 'number' && v.price > 0 && v.price !== line.unit) {
              line.unit = v.price;
              repriced++;
            }
            if (p.title) line.title = p.title;
            if (p.featured_image) line.image = p.featured_image;
          })
          .catch(function (status) { if (status === 404) { line._drop = true; dropped++; } });
      }).filter(Boolean)).then(function () {
        if (!repriced && !dropped) return;
        store.lines = store.lines.filter(function (l) { return !l._drop; });
        persist();

        var note = root.querySelector('[data-kmc-stale]');
        if (!note) return;
        var parts = [];
        if (repriced) parts.push(repriced === 1 ? 'One price has been updated' : repriced + ' prices have been updated');
        if (dropped)  parts.push(dropped === 1 ? 'one kit is no longer available and was removed' : dropped + ' kits are no longer available and were removed');
        note.textContent = parts.join(', ') + '. This is the current price.';
        note.hidden = false;
      });
    }

    render();
    revalidate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
